import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Award, Heart, Plus, Search, X, Sparkles } from 'lucide-react';
import { atlasApi, AtlasUser, Kudos, KudosValor } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const VALORES: { id: KudosValor; label: string; emoji: string; cor: string }[] = [
  { id: 'inovacao', label: 'Inovação', emoji: '💡', cor: 'bg-amber-100 text-amber-700 border-amber-200' },
  { id: 'cliente', label: 'Foco no Cliente', emoji: '🤝', cor: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'time', label: 'Trabalho em Time', emoji: '👥', cor: 'bg-purple-100 text-purple-700 border-purple-200' },
  { id: 'excelencia', label: 'Excelência', emoji: '⭐', cor: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { id: 'atitude', label: 'Atitude', emoji: '🔥', cor: 'bg-rose-100 text-rose-700 border-rose-200' },
  { id: 'entrega', label: 'Entrega', emoji: '🎯', cor: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
];

const valorMeta = (v: string) => VALORES.find(x => x.id === v) || VALORES[2];

const tempoRelativo = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const dias = Math.floor(h / 24);
  if (dias < 30) return `há ${dias}d`;
  return d.toLocaleDateString('pt-BR');
};

interface ColabResumo {
  funccodi: number;
  nome?: string;
  cargo?: string;
  tem_foto?: boolean;
  data_sinc?: string;
}

const KudosApp: React.FC<Props> = ({ user }) => {
  const [itens, setItens] = useState<Kudos[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [fb, setFb] = useState<Feedback>(null);

  const [modal, setModal] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [destino, setDestino] = useState<ColabResumo | null>(null);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [valor, setValor] = useState<KudosValor>('time');

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getKudosFeed(user, 80)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar reconhecimentos'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (!modal || destino) return;
    const termo = busca.trim();
    if (termo.length < 2) { setResultados([]); return; }
    let cancel = false;
    setBuscando(true);
    const t = window.setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: termo, status: 'ativo', per_page: 8 })
        .then((r: any) => { if (!cancel) setResultados(r?.itens || []); })
        .catch(() => { if (!cancel) setResultados([]); })
        .finally(() => { if (!cancel) setBuscando(false); });
    }, 300);
    return () => { cancel = true; window.clearTimeout(t); };
  }, [user, modal, destino, busca]);

  const abrir = () => {
    setDestino(null); setBusca(''); setResultados([]);
    setMensagem(''); setValor('time'); setFb(null); setModal(true);
  };

  const enviar = async () => {
    if (!destino || !mensagem.trim()) return;
    setSalvando(true);
    setFb(null);
    try {
      const r = await atlasApi.criarKudos(user, {
        para_funccodi: destino.funccodi,
        mensagem: mensagem.trim(),
        valor,
        publico: true,
      });
      setItens(prev => [r.kudos, ...prev]);
      setModal(false);
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao enviar' });
    } finally {
      setSalvando(false);
    }
  };

  const curtir = async (k: Kudos) => {
    // otimista
    setItens(prev => prev.map(x => x._id === k._id
      ? { ...x, curti: !x.curti, curtidas_count: x.curtidas_count + (x.curti ? -1 : 1) }
      : x));
    try {
      const r = await atlasApi.curtirKudos(user, k._id);
      setItens(prev => prev.map(x => x._id === k._id
        ? { ...x, curti: r.curti, curtidas_count: r.curtidas_count } : x));
    } catch {
      carregar();
    }
  };

  const stats = useMemo(() => {
    const porValor: Record<string, number> = {};
    itens.forEach(k => { porValor[k.valor] = (porValor[k.valor] || 0) + 1; });
    const top = Object.entries(porValor).sort((a, b) => b[1] - a[1])[0];
    return { total: itens.length, topValor: top ? valorMeta(top[0]) : null };
  }, [itens]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-3">
          <div className="bg-white rounded-2xl border border-gray-100 px-5 py-3 shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-xs text-gray-400">reconhecimentos</div>
          </div>
          {stats.topValor && (
            <div className="bg-white rounded-2xl border border-gray-100 px-5 py-3 shadow-sm">
              <div className="text-2xl font-bold text-gray-900">{stats.topValor.emoji}</div>
              <div className="text-xs text-gray-400">valor em alta: {stats.topValor.label}</div>
            </div>
          )}
        </div>
        <button
          onClick={abrir}
          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-sm transition"
        >
          <Plus className="w-4 h-4" /> Reconhecer alguém
        </button>
      </div>

      {erro && <FeedbackBanner feedback={{ tipo: 'erro', msg: erro }} />}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : itens.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Nenhum reconhecimento ainda. Seja o primeiro a reconhecer um colega!</p>
        </div>
      ) : (
        <div className="space-y-3 max-w-2xl">
          {itens.map(k => {
            const meta = valorMeta(k.valor);
            return (
              <div key={k._id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <AvatarColaborador
                    nome={k.de_nome}
                    fotoUrl={k.de_tem_foto ? atlasApi.fotoUrl(user, k.de_funccodi) : undefined}
                    size={40}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-700">
                      <span className="font-semibold text-gray-900">{k.de_nome || 'Alguém'}</span>
                      {' reconheceu '}
                      <span className="font-semibold text-gray-900">{k.para_nome || 'um colega'}</span>
                    </div>
                    <div className="text-xs text-gray-400">{tempoRelativo(k.criado_em)}</div>
                    <span className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cor}`}>
                      {meta.emoji} {meta.label}
                    </span>
                    <p className="text-gray-700 text-sm mt-2 whitespace-pre-wrap">{k.mensagem}</p>
                    <button
                      onClick={() => curtir(k)}
                      className={`inline-flex items-center gap-1.5 mt-3 text-xs font-medium transition ${
                        k.curti ? 'text-rose-600' : 'text-gray-400 hover:text-rose-500'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${k.curti ? 'fill-rose-600' : ''}`} />
                      {k.curtidas_count > 0 ? k.curtidas_count : ''} {k.curtidas_count === 1 ? 'curtida' : k.curtidas_count > 1 ? 'curtidas' : 'curtir'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" /> Reconhecer um colega
              </h3>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {fb && <FeedbackBanner feedback={fb} />}

              <div>
                <label className={labelCls}>Para quem?</label>
                {destino ? (
                  <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2">
                    <AvatarColaborador
                      nome={destino.nome}
                      fotoUrl={destino.tem_foto ? atlasApi.fotoUrl(user, destino.funccodi, destino.data_sinc) : undefined}
                      size={36}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{destino.nome}</div>
                      <div className="text-xs text-gray-400 truncate">{destino.cargo || '—'}</div>
                    </div>
                    <button onClick={() => setDestino(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      autoFocus
                      value={busca}
                      onChange={e => setBusca(e.target.value)}
                      placeholder="Buscar colaborador..."
                      className={`${inputCls} pl-9`}
                    />
                    {buscando && <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                    {resultados.length > 0 && (
                      <div className="mt-2 border border-gray-100 rounded-xl divide-y divide-gray-50 max-h-56 overflow-y-auto">
                        {resultados.map((c: any) => (
                          <button
                            key={c._id || c.funccodi}
                            onClick={() => setDestino({
                              funccodi: c.funccodi,
                              nome: c.dados_publicos?.nome,
                              cargo: c.dados_publicos?.cargo_nome,
                              tem_foto: c.dados_publicos?.tem_foto_gix,
                              data_sinc: c.data_sincronizacao,
                            })}
                            className="flex items-center gap-3 w-full px-3 py-2 hover:bg-purple-50/50 text-left"
                          >
                            <AvatarColaborador
                              nome={c.dados_publicos?.nome}
                              fotoUrl={c.dados_publicos?.tem_foto_gix ? atlasApi.fotoUrl(user, c.funccodi, c.data_sincronizacao) : undefined}
                              size={32}
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">{c.dados_publicos?.nome}</div>
                              <div className="text-xs text-gray-400 truncate">{c.dados_publicos?.cargo_nome || '—'}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls}>Valor reconhecido</label>
                <div className="flex flex-wrap gap-2">
                  {VALORES.map(v => (
                    <button
                      key={v.id}
                      onClick={() => setValor(v.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                        valor === v.id ? v.cor + ' ring-2 ring-offset-1 ring-purple-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {v.emoji} {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Mensagem</label>
                <textarea
                  value={mensagem}
                  onChange={e => setMensagem(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Conte o que essa pessoa fez de especial..."
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                Cancelar
              </button>
              <button
                onClick={enviar}
                disabled={!destino || !mensagem.trim() || salvando}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium"
              >
                {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Enviar reconhecimento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KudosApp;
